import {
  useMutation,
  useQuery,
  useQueryClient,
  UseQueryResult,
} from "react-query";
import CustomerApiService, { ApiError } from "../../../CustomerApiService";
import { GamebookPlay } from "../gamebook/service";
import {
  FIXTURE_PROXY_URL,
  filmsGroundFixturesEnabled,
  getFixtureJob,
  getFixtureKeyframeUrl,
  getFixtureNeighborClip,
  listFixtureJobSummaries,
  submitFixtureAnchors,
  submitFixtureStart,
} from "./__fixtures__/anchorJobFixtures";

/**
 * Films Ground API calls — mirrors `../gamebook/service.ts`'s shape exactly (thin wrappers over
 * `CustomerApiService.fetch`, a query-key object, and the actual `useQuery`/`useMutation` hooks
 * screens call, not the raw fetch functions) — see `apps/customer-frontend/CLAUDE.md`'s "Data
 * fetching (react-query)" section for why this is the house convention.
 *
 * FIXTURES: per the plan's staged build order, the frontend is built and validated against
 * fixture data before the backend routes exist (Stage 2 before Stage 1/3). Every function below
 * checks `filmsGroundFixturesEnabled()` (gated on `REACT_APP_FILMS_GROUND_FIXTURES=1`, unset in
 * every tracked `.env*` file — opt-in only, never on by default) and returns canned data instead
 * of calling `CustomerApiService` when it's set. Flip it off (the default) once the real
 * `filmsGround.ts`/`filmsGroundData.ts` routes land (plan Stage 1-4) — no other change needed
 * here, the wire shapes already match the plan's data model exactly.
 */

const PREFIX = "/spotter/anchor-jobs";

/** Same interval `../gamebook/service.ts`'s `useGamebookMatchup` polls at while a triggered
 *  generation is pending. */
export const FILMS_GROUND_POLL_INTERVAL_MS = 5_000;

export type AnchorJobStatus =
  | "awaiting_start"
  | "analyzing"
  | "needs_confirmation"
  | "submitted";

/** Which portal surface a job renders — mirrors the backend's `FilmsGroundJobMode`. Top Films
 *  reuses the awaiting_start step verbatim and never sees the other three statuses' anchor-review
 *  UI (its `POST .../start` goes straight to `submitted`, with an empty `anchors[]`). */
export type AnchorJobMode = "films_ground" | "top_films";

/** Which copy/labels the confirm flow renders — mirrors the backend's `FilmsGroundReviewMode`
 *  (`apps/customer-api/src/services/filmsGroundStore.ts`). Orthogonal to `AnchorJobMode` above: a slow-mo
 *  band job is always `mode: "films_ground"`, never `"top_films"`. Never `undefined` here — an
 *  absent value on the wire is normalized to `"films_ground"` server-side. */
export type AnchorJobReviewMode = "films_ground" | "slowmo_band";

/** A slow-mo job's identity within its parent game run — present only alongside
 *  `reviewMode: "slowmo_band"`. `bandId` is n8n's opaque identifier (never parsed here);
 *  `startClipAssetId`/`endClipAssetId` bound the slice of the game reel this band covers, and are
 *  genuine members of this job's own reel — `useProxyUrl` previews them like any anchor's clip.
 *  Mirrors the backend's `FilmsGroundBandInfo`. */
export interface AnchorJobBand {
  bandId: string;
  startClipAssetId: string;
  endClipAssetId: string;
  /** Human-readable band identity (Monday 12921838919, Seth: quarter + clip count, e.g.
   *  `"Slow-mo · 4th quarter (20 clips)"`) — the thing that tells two concurrent bands over the
   *  same source collection apart, unlike `bandId`. Optional and rendered verbatim (never
   *  reformatted — Seth owns the wording): absent on a band job n8n hasn't upgraded to send it
   *  yet, in which case that job renders exactly as it always has. */
  label?: string;
}

/** One gamebook row — mirrors the existing `GamebookPlay` shape's `uid`/`quarter`/summary fields
 *  (a lite projection; Films Ground doesn't need the rest of `GamebookPlay`'s box-score fields).
 *  `yardsGainedOrLost` is response-only (customer-apps#136, `filmsGround.ts`'s `toWireJob`) —
 *  OMITTED, not `null`, when a play has nothing on record; a genuine 0-yard play arrives as the
 *  string `"0"`. See `gamebookRowFormat.ts`'s `formatYardage` for the render rule. */
export interface GamebookPlayLite {
  uid: string;
  quarter: number;
  summary: string;
  yardsGainedOrLost?: string;
}

/** Set once by Phase A's submit (`POST /anchor-jobs/:jobId/start`) — the confirmed game-start
 *  boundary. Immutable from Phase B (no "Change" affordance here — revising the start boundary is
 *  Phase A's job, not exposed again once analysis has started). */
export interface AnchorJobStart {
  assetId: string;
  uid: string;
  summaryText: string;
}

export type AnchorResolvedSource = "picker" | "manual";

export interface AnchorTask {
  /** Stable recording-order position, 1..`totalAnchors` — assigned by n8n at analysis time, never
   *  reassigned client-side. */
  anchorId: number;
  clipAssetId: string;
  /** The camera clip's own filename (e.g. `"A012_C0031"`) — n8n-supplied, already on the wire
   *  (`filmsGround.ts`'s `toWireJob`); the reel strip is the first surface that needs a clip
   *  identity distinct from its gamebook UID. Absent/blank on a stored anchor row with no clip
   *  name recorded — `reelCardLabel` (`reelCard.ts`) falls back to the anchor index. */
  clipName?: string;
  /** n8n's best-guess gamebook UID for this anchor — always stays pinned to this value; stepping
   *  through neighbors never mutates it (mockup bug #10, not ported). */
  spotterPickUid: string;
  /** The ±4-play window this anchor's picker renders from. Always present, even once resolved —
   *  reopening a resolved anchor to re-pick needs it again. */
  gamebookWindow: GamebookPlayLite[];
  resolvedUid: string | null;
  resolvedClipAssetId: string | null;
  /** `false` until a human commits a pick. Protects n8n retry writes from clobbering a
   *  customer's already-confirmed anchor (server-side; not meaningful to check client-side). */
  resolvedByUser: boolean;
  resolvedSource: AnchorResolvedSource | null;
}

/** One clip from the job's own reel (its iconik collection), already signed for playback —
 *  `useNeighborClip`'s return shape. `null` means the boundary of the reel in that direction, not
 *  an error (see `AnchorStage.tsx`'s neighbor-nav). */
export interface NeighborClip {
  assetId: string;
  title: string;
  url: string;
  expiresAt: string;
}

/** What a job was run against — mirrors the backend's `FilmsGroundJobSourceWire`
 *  (`apps/customer-api/src/routes/spotter/filmsGround.ts`), itself deliberately the same
 *  kind/name/url/assetCount shape the Control Room already renders for its own `target`
 *  (Monday 12871494046, Seth: "this matches the three display cases you're already showing in
 *  the consolidated Control Room"). `name`/`url` only apply to `collection`/`saved_search`;
 *  `assetCount` only to `assets`. */
export type AnchorJobSourceKind = "collection" | "saved_search" | "assets";

export interface AnchorJobSource {
  kind: AnchorJobSourceKind;
  name?: string;
  url?: string;
  assetCount?: number;
}

/** `GET /anchor-jobs` list row — meta only, no anchors. Originally the old `AnchorJobPicker.tsx`
 *  tab strip's own data source; that component is gone (design A4, Monday 12936679727), but this
 *  type/`useAnchorJobs` below are not dead — `ControlRoomScreen.tsx`'s rail rows now read this
 *  same list to resolve each review-bearing job's specific `AnchorJobStatus` badge. */
export interface AnchorJobSummary {
  jobId: string;
  status: AnchorJobStatus;
  mode: AnchorJobMode;
  source: AnchorJobSource;
  gameLabel: string;
  /** The collection one level up from a `collection`-kind source's id — the job tab strip's
   *  subtitle (Monday 12871494046, Seth: repeating the source name there was redundant). `null`
   *  for a root-level collection, a non-collection source (saved search/assets have no "parent"
   *  concept), or an unresolvable one; render nothing rather than a UUID or a duplicate title. */
  parentCollectionName: string | null;
  /** `band.label` (see `AnchorJobBand`'s doc comment) — `null` for a plain Films Ground job AND
   *  for a band job that has no label yet. Present on the SUMMARY, not just the full `AnchorJob`,
   *  because two concurrent band jobs over the same source collection are otherwise
   *  indistinguishable in any job-level list — originally for the old tab strip's own rows, now
   *  unused by anything (`ControlRoomScreen.tsx`'s rail rows resolve their own label via
   *  `rowLabel.ts` instead), kept on the wire shape rather than trimmed since it costs nothing to
   *  carry and a future rail-row enhancement may want it again. */
  bandLabel: string | null;
  totalAnchors: number;
}

export interface AnchorJob {
  jobId: string;
  status: AnchorJobStatus;
  mode: AnchorJobMode;
  /** See `AnchorJobReviewMode`'s doc comment — which review surface Phase B renders. */
  reviewMode: AnchorJobReviewMode;
  /** Only present when `reviewMode === "slowmo_band"`; `null` for every plain Films Ground job. */
  band: AnchorJobBand | null;
  source: AnchorJobSource;
  /** The collection one level up from a `collection`-kind source — same join
   *  `AnchorJobSummary.parentCollectionName` already carries, also on the single-job read:
   *  a collection is never named alone (clubs name footage folders after
   *  the camera that shot them), so the full-page editor's breadcrumb needs this to show
   *  "2026 Preseason › ARRI A012", not "ARRI A012" alone. `null` for a root-level collection, a
   *  non-collection source, or on any lookup failure — see `anchorJobParentLabel`
   *  (`breadcrumbParent.ts`) for what a `saved_search`/`assets` source renders in this slot
   *  instead (a saved search has no parent collection to resolve; render the literal string
   *  "Saved Search"). */
  parentCollectionName: string | null;
  gameLabel: string;
  start: AnchorJobStart | null;
  /** P001-P010 (or however many precede kickoff) — Phase A's picker source. Populated by n8n at
   *  job-creation time, before any analysis has run. */
  firstTenPlays: GamebookPlayLite[];
  /** The FULL, ordered `master_play_by_play` for this job's game — the same
   *  real gamebook data (and the same `GamebookPlay` shape) `../gamebook/GamebookScreen.tsx`
   *  reads, not n8n's own reduced `gamebookWindow` (`AnchorTask` below, kept for the Phase A start
   *  picker and unchanged). The full-page anchor editor's gamebook band renders straight from
   *  this list — every column (situation/flags/yardage) reuses `gamebookCellContent.tsx`'s cell
   *  renderers unmodified, since the shape is identical to what the Gamebook tab already renders.
   *  Empty on a game with nothing ingested yet, or on a best-effort join failure server-side —
   *  never a broken job read over it. */
  plays: GamebookPlay[];
  totalAnchors: number;
  /** Empty until `status === "needs_confirmation"`. */
  anchors: AnchorTask[];
}

export interface SubmitAnchorPayload {
  anchorId: number;
  uid: string;
  clipAssetId: string;
  source: AnchorResolvedSource;
}

/** The error code the backend's server-side ordering re-check (plan REQ-5) rejects a Phase B
 *  submit with — `ApiError.code === OUT_OF_ORDER_CODE`. The screen's own `checkOrder()` already
 *  blocks the Submit button before a genuinely out-of-order payload could ever reach this call, so
 *  hitting this in practice means a stale/raced submit slipped past the client-side gate (e.g. a
 *  retried request) — the screen re-runs `checkOrder()` locally to re-highlight the offending
 *  cards rather than trusting a wire-carried anchor-id list (`ApiError` doesn't currently surface
 *  arbitrary `extra` fields beyond `code` — see `CustomerApiService.ts`'s `handleResponse`). Not
 *  reachable in fixture mode (see `useSubmitAnchors` below) since there is no server there to
 *  reject anything. */
export const OUT_OF_ORDER_CODE = "OUT_OF_ORDER";

async function getAnchorJobs(): Promise<AnchorJobSummary[]> {
  if (filmsGroundFixturesEnabled()) {
    return listFixtureJobSummaries();
  }
  const { jobs } = await CustomerApiService.fetch<{
    jobs: AnchorJobSummary[];
  }>("GET", PREFIX);
  return jobs;
}

async function getAnchorJob(jobId: string): Promise<AnchorJob> {
  if (filmsGroundFixturesEnabled()) {
    const fixture = getFixtureJob(jobId);
    if (!fixture) {
      throw new Error(`No fixture job for "${jobId}"`);
    }
    return fixture;
  }
  const { job } = await CustomerApiService.fetch<{ job: AnchorJob }>(
    "GET",
    `${PREFIX}/${encodeURIComponent(jobId)}`
  );
  return job;
}

async function submitStart(
  jobId: string,
  assetId: string,
  uid: string
): Promise<{ ok: true }> {
  if (filmsGroundFixturesEnabled()) {
    submitFixtureStart(jobId, assetId, uid);
    return { ok: true };
  }
  return CustomerApiService.fetch<{ ok: true }>(
    "POST",
    `${PREFIX}/${encodeURIComponent(jobId)}/start`,
    { assetId, uid }
  );
}

async function submitAnchors(
  jobId: string,
  anchors: SubmitAnchorPayload[]
): Promise<AnchorJob> {
  if (filmsGroundFixturesEnabled()) {
    const submitted = submitFixtureAnchors(jobId);
    if (!submitted) {
      throw new Error(`No fixture job for "${jobId}"`);
    }
    return submitted;
  }
  const { job } = await CustomerApiService.fetch<{ job: AnchorJob }>(
    "POST",
    `${PREFIX}/${encodeURIComponent(jobId)}/anchors`,
    { anchors }
  );
  return job;
}

/** `true` when `error` is the backend's server-side ordering rejection (`OUT_OF_ORDER_CODE`) —
 *  the screen checks this to show the same "out of order" banner `checkOrder()` shows client-side,
 *  rather than a generic save-failed message. */
export function isOutOfOrderError(error: unknown): boolean {
  return error instanceof ApiError && error.code === OUT_OF_ORDER_CODE;
}

/** Same "never show a stale message for a 401" rule as `../gamebook/GamebookScreen.tsx`'s own
 *  `mutationErrorMessage` — a 401 is about to be superseded by `useInterfaceAccess`'s "denied"
 *  notice anyway (see that function's doc comment for the full reasoning), including its
 *  empty-message fallback. Kept as a local copy rather than an import across feature folders. */
export function filmsGroundMutationErrorMessage(
  error: unknown,
  fallback: string
): string {
  return error instanceof ApiError && error.status !== 401 && error.message
    ? error.message
    : fallback;
}

async function getProxyUrl(
  jobId: string,
  assetId: string
): Promise<{ url: string; expiresAt: string }> {
  if (filmsGroundFixturesEnabled()) {
    return FIXTURE_PROXY_URL;
  }
  return CustomerApiService.fetch<{ url: string; expiresAt: string }>(
    "GET",
    `${PREFIX}/${encodeURIComponent(jobId)}/clips/${encodeURIComponent(
      assetId
    )}/proxy-url`
  );
}

/** A cheap still image for `assetId` — the reel carousel's per-clip thumbnail and the stage
 *  player's pre-load poster, never the full video proxy `getProxyUrl` mints. `null` when iconik
 *  has no keyframe for the clip yet (a real, expected state, same "not an error" treatment
 *  `getNeighborClip` gives a reel boundary) — callers fall back to the plain placeholder. Fixture
 *  mode has no real iconik to mint one from, so it renders `getFixtureKeyframeUrl`'s generated
 *  placeholder image instead of a real signed URL — enough to exercise the actual `<img>`
 *  rendering path in fixture mode rather than only ever hitting the "no thumbnail" branch. */
async function getKeyframeUrl(
  jobId: string,
  assetId: string
): Promise<{ url: string; expiresAt: string } | null> {
  if (filmsGroundFixturesEnabled()) {
    return getFixtureKeyframeUrl(assetId);
  }
  try {
    return await CustomerApiService.fetch<{ url: string; expiresAt: string }>(
      "GET",
      `${PREFIX}/${encodeURIComponent(jobId)}/clips/${encodeURIComponent(
        assetId
      )}/keyframe-url`
    );
  } catch (e) {
    if (e instanceof ApiError && e.status === 404) {
      return null;
    }
    throw e;
  }
}

/** The clip immediately before/after `assetId` in the job's own reel (see `AnchorStage.tsx`'s
 *  neighbor-nav doc comment) — already a signed, playable URL, so the caller never needs a second
 *  round trip. `null` at either end of the reel, OR when the job has no `gameActionShots` synced
 *  yet — both a real, expected "no neighbours" state, not an error. `showAll` is the explicit
 *  "show all clips" escape hatch (default `false` — skip `play_visible: false` shots). */
async function getNeighborClip(
  jobId: string,
  assetId: string,
  direction: "prev" | "next",
  showAll: boolean
): Promise<NeighborClip | null> {
  if (filmsGroundFixturesEnabled()) {
    return getFixtureNeighborClip(assetId, direction);
  }
  const { clip } = await CustomerApiService.fetch<{
    clip: NeighborClip | null;
  }>(
    "GET",
    `${PREFIX}/${encodeURIComponent(jobId)}/clips/${encodeURIComponent(
      assetId
    )}/neighbor?direction=${direction}&showAll=${showAll}`
  );
  return clip;
}

export const filmsGroundQueryKeys = {
  jobs: ["films-ground-jobs"] as const,
  job: (jobId: string) => ["films-ground-job", jobId] as const,
  proxyUrl: (jobId: string, assetId: string) =>
    ["films-ground-proxy-url", jobId, assetId] as const,
  keyframeUrl: (jobId: string, assetId: string) =>
    ["films-ground-keyframe-url", jobId, assetId] as const,
  neighborClip: (
    jobId: string,
    assetId: string,
    direction: "prev" | "next",
    showAll: boolean
  ) =>
    ["films-ground-neighbor-clip", jobId, assetId, direction, showAll] as const,
};

/** Every Films Ground job for the org, any status, not just `awaiting_start` ones. Originally the
 *  old `AnchorJobPicker.tsx` tab strip's own data source (now deleted, design A4); its sole
 *  caller today is `ControlRoomScreen.tsx`'s rail, which reads it for each review-bearing row's
 *  specific `AnchorJobStatus` badge (`reviewStatusByJobId`/`ReviewStatusBadge`). */
export function useAnchorJobs(): UseQueryResult<AnchorJobSummary[], Error> {
  return useQuery<AnchorJobSummary[], Error>(
    filmsGroundQueryKeys.jobs,
    getAnchorJobs,
    { staleTime: 30_000 }
  );
}

/** `poll: true` re-fetches every `FILMS_GROUND_POLL_INTERVAL_MS` — Phase A passes this while
 *  `status === "analyzing"` so the screen picks up `needs_confirmation` (or a Top-Films-style
 *  `submitted`, skipping confirmation entirely) on its own. */
export function useAnchorJob(
  jobId: string | undefined,
  poll = false
): UseQueryResult<AnchorJob, Error> {
  return useQuery<AnchorJob, Error>(
    filmsGroundQueryKeys.job(jobId ?? ""),
    () => getAnchorJob(jobId as string),
    {
      enabled: !!jobId,
      refetchInterval: poll ? FILMS_GROUND_POLL_INTERVAL_MS : undefined,
    }
  );
}

/** Mints a signed proxy URL for one clip, on demand only (`enabled`) — never eagerly for every
 *  anchor on page load (plan §4.3). */
export function useProxyUrl(
  jobId: string | undefined,
  assetId: string | undefined,
  options: { enabled: boolean }
): UseQueryResult<{ url: string; expiresAt: string }, Error> {
  return useQuery<{ url: string; expiresAt: string }, Error>(
    filmsGroundQueryKeys.proxyUrl(jobId ?? "", assetId ?? ""),
    () => getProxyUrl(jobId as string, assetId as string),
    {
      enabled: options.enabled && !!jobId && !!assetId,
      staleTime: 5 * 60_000,
    }
  );
}

/** A clip's thumbnail — unlike `useProxyUrl`, this IS meant to be requested for every visible reel
 *  card (a keyframe is a cheap still image, not a video stream); `options.enabled` still gates it
 *  so `ReelStrip.tsx` can request one only once a card actually scrolls into view rather than for
 *  the whole reel on mount. `staleTime` matches `useProxyUrl`'s own (signed URLs are good for a
 *  while, no need to re-mint on every remount). */
export function useClipThumbnail(
  jobId: string | undefined,
  assetId: string | undefined,
  options: { enabled: boolean }
): UseQueryResult<{ url: string; expiresAt: string } | null, Error> {
  return useQuery<{ url: string; expiresAt: string } | null, Error>(
    filmsGroundQueryKeys.keyframeUrl(jobId ?? "", assetId ?? ""),
    () => getKeyframeUrl(jobId as string, assetId as string),
    {
      enabled: options.enabled && !!jobId && !!assetId,
      staleTime: 5 * 60_000,
    }
  );
}

/** Resolves the next/previous clip in the job's own reel from `assetId`, on demand only
 *  (`enabled`) — walking the reel is user-driven (a button click per step), never eager. Cached
 *  per (job, assetId, direction) so re-visiting a clip already stepped to this session doesn't
 *  re-hit iconik. */
export function useNeighborClip(
  jobId: string | undefined,
  assetId: string | undefined,
  direction: "prev" | "next",
  showAll: boolean,
  options: {
    enabled: boolean;
    onSuccess?: (clip: NeighborClip | null) => void;
  }
): UseQueryResult<NeighborClip | null, Error> {
  return useQuery<NeighborClip | null, Error>(
    filmsGroundQueryKeys.neighborClip(
      jobId ?? "",
      assetId ?? "",
      direction,
      showAll
    ),
    () =>
      getNeighborClip(jobId as string, assetId as string, direction, showAll),
    {
      enabled: options.enabled && !!jobId && !!assetId,
      staleTime: 5 * 60_000,
      onSuccess: options.onSuccess,
    }
  );
}

export function useSubmitStart(jobId: string) {
  const queryClient = useQueryClient();
  return useMutation(
    (payload: { assetId: string; uid: string }) =>
      submitStart(jobId, payload.assetId, payload.uid),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(filmsGroundQueryKeys.job(jobId));
        queryClient.invalidateQueries(filmsGroundQueryKeys.jobs);
      },
    }
  );
}

/** Write-through on success (`setQueryData`, mirrors `fields/service.ts`'s convention) so the
 *  screen goes terminal immediately without waiting on a refetch. */
export function useSubmitAnchors(jobId: string) {
  const queryClient = useQueryClient();
  return useMutation(
    (anchors: SubmitAnchorPayload[]) => submitAnchors(jobId, anchors),
    {
      onSuccess: (job) => {
        queryClient.setQueryData(filmsGroundQueryKeys.job(jobId), job);
        queryClient.invalidateQueries(filmsGroundQueryKeys.jobs);
      },
    }
  );
}

/** `NewRunModal.tsx`'s own source shape (Monday 12921140208) — id(s) only, mirrors the backend's
 *  `FilmsGroundIntakeSource` (`filmsGroundGenerate.ts`): unlike `AnchorJobSource` above (an
 *  ALREADY-RUNNING job's source, resolved server-side to a display name/link), starting a run has
 *  no name to show — the customer just pastes a link, the id is all `POST /anchor-jobs` needs. */
export type NewRunSource =
  | { kind: "collection"; collectionId: string }
  | { kind: "saved_search"; savedSearchId: string }
  | { kind: "assets"; assetIds: string[] };

/** `NewRunModal.tsx`'s ingest metadata, keyed by iconik metadata name (`../controlRoom/
 *  ingestFields.ts`'s `buildIngestPayload`) — the stats-automation fields Seth's downstream
 *  router needs (Monday 12930235017), sent alongside the existing source/season/week. */
export type NewRunIngest = Record<string, string | string[] | boolean>;

/** POST `/anchor-jobs` (no `:jobId`) — kicks off a brand-new run against an already-generated
 *  Gamebook week. No fixture path: this is a write with no meaningful canned response (unlike the
 *  read-side fixtures above, built for Stage 2 frontend-before-backend development), and the real
 *  route has existed since this same round. Returns nothing to write into the cache — no jobId
 *  comes back (n8n mints it, same as every job in this store always has), so there's nothing new
 *  to optimistically add; `NewRunModal`'s caller just invalidates the jobs list and relies on
 *  Control Room's own unconditional 5s poll to pick the new job up once n8n's first status push
 *  lands (see `../controlRoom/service.ts`'s doc comment on why that poll is unconditional). */
async function startNewRun(
  source: NewRunSource,
  season: number,
  week: string,
  ingest: NewRunIngest
): Promise<void> {
  await CustomerApiService.fetch<{ ok: boolean }>("POST", PREFIX, {
    source,
    season,
    week,
    ingest,
  });
}

export function useStartNewRun() {
  return useMutation(
    (payload: {
      source: NewRunSource;
      season: number;
      week: string;
      ingest: NewRunIngest;
    }) =>
      startNewRun(payload.source, payload.season, payload.week, payload.ingest)
  );
}
